// One landing page per prop firm (FirmPage.js, and prerender.js for search engines).
// The rule tables come from the presets in balance.js, so the pages always match the
// journal. Only the firm-specific words live here. `firm` is the preset's firm name.
export const FIRM_PAGES = [
  {
    slug: 'topstep-trading-journal',
    firm: 'Topstep',
    name: 'Topstep',
    en: {
      intro: 'Track your Topstep evaluation and funded account the way Topstep measures them: the end-of-day trailing max loss, the 55% consistency target and the winning days you need for a payout, updated every time you import your TopstepX trades.',
      watch: [
        'The max loss limit trails your end-of-day balance and stops once it reaches your starting balance. Losses during the day only count at the close, but a big red day still moves you closer.',
        'In the evaluation, your best day can be at most 55% of your total profit. One huge day can push your real target higher than the number on the dashboard.',
        'On a funded account, standard payouts need 5 winning days of at least $150. After a payout, those days start counting again.',
      ],
    },
    es: {
      intro: 'Sigue tu evaluación y tu cuenta fondeada de Topstep como Topstep las mide: la pérdida máxima móvil de fin de día, el objetivo de consistencia del 55% y los días ganadores que necesitas para un pago, actualizados cada vez que importas tus operaciones de TopstepX.',
      watch: [
        'La pérdida máxima sigue tu saldo de cierre y deja de moverse cuando llega a tu saldo inicial. Las pérdidas dentro del día solo cuentan al cierre, pero un gran día rojo igual te acerca.',
        'En la evaluación, tu mejor día puede ser como máximo el 55% de tu ganancia total. Un día enorme puede subir tu objetivo real por encima del número del panel.',
        'En una cuenta fondeada, los pagos estándar piden 5 días ganadores de al menos $150. Después de un pago, esos días vuelven a contar desde cero.',
      ],
    },
  },
  {
    slug: 'apex-trading-journal',
    firm: 'Apex',
    name: 'Apex',
    fullName: 'Apex Trader Funding',
    en: {
      intro: 'Track your Apex Trader Funding evaluations, end-of-day or intraday, with the trailing threshold, the point where it stops trailing and, on end-of-day plans, the daily loss limit, all from your own trades.',
      watch: [
        'The trailing threshold stops once it reaches your starting balance plus $100. Until then, every new high moves it up.',
        'Intraday plans trail in real time, including open profit you give back. Your file only has closed trades, so Market Matrix shows an estimate and reminds you to keep extra room.',
        'End-of-day plans add a daily loss limit. Knowing what you have left today matters as much as the max loss.',
      ],
    },
    es: {
      intro: 'Sigue tus evaluaciones de Apex Trader Funding, de fin de día o intradía, con la pérdida máxima móvil, el punto donde deja de moverse y, en los planes de fin de día, el límite de pérdida diaria, todo a partir de tus propias operaciones.',
      watch: [
        'La pérdida máxima móvil deja de moverse cuando llega a tu saldo inicial más $100. Hasta entonces, cada nuevo máximo la sube.',
        'Los planes intradía la mueven en tiempo real, incluida la ganancia abierta que devuelves. Tu archivo solo tiene operaciones cerradas, así que Market Matrix muestra una estimación y te recuerda dejar margen extra.',
        'Los planes de fin de día agregan un límite de pérdida diaria. Saber cuánto te queda hoy importa tanto como la pérdida máxima.',
      ],
    },
  },
  {
    slug: 'myfundedfutures-trading-journal',
    firm: 'MyFundedFutures',
    name: 'MyFundedFutures',
    en: {
      intro: 'Track your MyFundedFutures Rapid or Pro evaluation with the end-of-day trailing drawdown and the 50% consistency rule, so you always know how far you are from passing and from your max loss.',
      watch: [
        'The drawdown trails your end-of-day balance. A strong close raises it, so protect your gains near the end of the session.',
        'Your best day can be at most 50% of your total profit. A single big day can mean you need more profit than the target before you pass.',
        'Rapid and Pro plans have different sizes and targets, so set up each account with the right preset.',
      ],
    },
    es: {
      intro: 'Sigue tu evaluación Rapid o Pro de MyFundedFutures con la pérdida máxima móvil de fin de día y la regla de consistencia del 50%, para saber siempre qué tan lejos estás de aprobar y de tu pérdida máxima.',
      watch: [
        'La pérdida máxima sigue tu saldo de cierre. Un buen cierre la sube, así que protege tus ganancias cerca del final de la sesión.',
        'Tu mejor día puede ser como máximo el 50% de tu ganancia total. Un solo día grande puede significar que necesitas más ganancia que el objetivo para aprobar.',
        'Los planes Rapid y Pro tienen tamaños y objetivos distintos, así que configura cada cuenta con la plantilla correcta.',
      ],
    },
  },
  {
    slug: 'lucid-trading-journal',
    firm: 'Lucid',
    name: 'Lucid',
    fullName: 'Lucid Trading',
    en: {
      intro: 'Track your Lucid Trading account, Pro, Flex, Daily or Direct, with its end-of-day drawdown that stops trailing at your starting balance plus $100, and the consistency rule for your plan, so you always know how far you are from passing and from your max loss.',
      watch: [
        'The drawdown trails your closing balance, not every tick, and stops at your starting balance plus $100. Daily Intraday and Daily funded accounts trail in real time instead.',
        'Consistency depends on the plan: Flex passes with no day over 40% of total profit and Daily with 50%. Pro has none in the evaluation but 40% for payouts, and Direct 20%.',
        'The daily loss limit is optional on Pro, Flex and Daily. If you bought it, add it to the account so the journal warns you before you hit it.',
      ],
    },
    es: {
      intro: 'Sigue tu cuenta de Lucid Trading, Pro, Flex, Daily o Direct, con su pérdida máxima de fin de día que deja de moverse en tu saldo inicial más $100, y la regla de consistencia de tu plan, para saber siempre qué tan lejos estás de aprobar y de tu pérdida máxima.',
      watch: [
        'La pérdida máxima sigue tu saldo de cierre, no cada tick, y se detiene en tu saldo inicial más $100. Las cuentas Daily Intraday y Daily fondeadas se mueven en tiempo real.',
        'La consistencia depende del plan: Flex se aprueba sin ningún día por encima del 40% de la ganancia total y Daily con el 50%. Pro no tiene en la evaluación, pero sí 40% para los retiros, y Direct 20%.',
        'El límite de pérdida diaria es opcional en Pro, Flex y Daily. Si lo compraste, agrégalo a la cuenta para que el diario te avise antes de llegar a él.',
      ],
    },
  },
  {
    slug: 'tradeify-trading-journal',
    firm: 'Tradeify',
    name: 'Tradeify',
    en: {
      intro: 'Track your Tradeify Growth evaluation with its end-of-day trailing drawdown and daily loss limit, so you see both how much room is left today and how much is left overall.',
      watch: [
        'The drawdown trails your end-of-day balance, so a strong close moves your max loss level up.',
        'The daily loss limit is separate from the max loss. On a red day, check both before the next trade.',
        "There's no consistency rule in the evaluation, but steady days still keep you far from the limits.",
      ],
    },
    es: {
      intro: 'Sigue tu evaluación Growth de Tradeify con su pérdida máxima móvil de fin de día y su límite de pérdida diaria, para ver cuánto margen te queda hoy y cuánto te queda en total.',
      watch: [
        'La pérdida máxima sigue tu saldo de cierre, así que un buen cierre sube tu nivel de pérdida máxima.',
        'El límite de pérdida diaria es aparte de la pérdida máxima. En un día rojo, revisa los dos antes de la siguiente operación.',
        'La evaluación no tiene regla de consistencia, pero los días constantes igual te mantienen lejos de los límites.',
      ],
    },
  },
  {
    slug: 'tradeday-trading-journal',
    firm: 'TradeDay',
    name: 'TradeDay',
    en: {
      intro: "Track your TradeDay evaluation with its end-of-day trailing drawdown and its consistency rule, which TradeDay measures against the profit target rather than your total profit.",
      watch: [
        "No single day can be more than 30% of the profit target. On a 50K account with a $3,000 target, that's a $900 cap per day, and it doesn't grow as you make more.",
        'The drawdown trails your end-of-day balance.',
        "There's no daily loss limit, so set your own daily stop.",
      ],
    },
    es: {
      intro: 'Sigue tu evaluación de TradeDay con su pérdida máxima móvil de fin de día y su regla de consistencia, que TradeDay mide contra el objetivo de ganancia y no contra tu ganancia total.',
      watch: [
        'Ningún día puede ser más del 30% del objetivo de ganancia. En una cuenta de 50K con objetivo de $3,000, eso es un tope de $900 por día, y no crece aunque ganes más.',
        'La pérdida máxima sigue tu saldo de cierre.',
        'No hay límite de pérdida diaria, así que pon tu propio stop diario.',
      ],
    },
  },
  {
    slug: 'take-profit-trader-trading-journal',
    firm: 'Take Profit Trader',
    name: 'Take Profit Trader',
    en: {
      intro: 'Track your Take Profit Trader Test and PRO account the way the firm measures them: the end-of-day trailing max loss in the Test, the 50% consistency rule, and the real-time drawdown once you reach PRO.',
      watch: [
        "In the Test, your best day has to stay under 50% of your total profit. It doesn't fail you, but one big day raises the profit you need before you pass.",
        "There's no daily loss limit, so a bad day can run all the way to your max loss. Set your own daily stop and let the journal hold you to it.",
        'On a PRO account the drawdown trails in real time, including open profit you give back. Your file only has closed trades, so Market Matrix shows an estimate and reminds you to keep extra room.',
      ],
    },
    es: {
      intro: 'Sigue tu Test y tu cuenta PRO de Take Profit Trader como la firma las mide: la pérdida máxima móvil de fin de día en el Test, la regla de consistencia del 50% y la pérdida máxima en tiempo real cuando llegas a PRO.',
      watch: [
        'En el Test, tu mejor día tiene que quedar por debajo del 50% de tu ganancia total. No te reprueba, pero un día grande sube la ganancia que necesitas para aprobar.',
        'No hay límite de pérdida diaria, así que un mal día puede llegar hasta tu pérdida máxima. Pon tu propio stop diario y deja que el diario te ayude a respetarlo.',
        'En una cuenta PRO la pérdida máxima se mueve en tiempo real, incluida la ganancia abierta que devuelves. Tu archivo solo tiene operaciones cerradas, así que Market Matrix muestra una estimación y te recuerda dejar margen extra.',
      ],
    },
  },
  {
    slug: 'alpha-futures-trading-journal',
    firm: 'Alpha Futures',
    name: 'Alpha Futures',
    en: {
      intro: 'Track your Alpha Futures Zero, Standard or Advanced evaluation with its end-of-day trailing loss limit, the daily loss guard on Zero and the consistency rule on Standard and Advanced, all from your own trades.',
      watch: [
        'Every plan trails your end-of-day balance and stops once it reaches your starting balance. A strong close moves your max loss level up.',
        'Standard caps your best day at 50% of your total profit and Advanced at 40%, so steady days get you there faster than one big one.',
        'Zero has no consistency rule in the evaluation, but it has a daily loss guard. On a red day, check what is left today before the next trade.',
      ],
    },
    es: {
      intro: 'Sigue tu evaluación Zero, Standard o Advanced de Alpha Futures con su pérdida máxima móvil de fin de día, el límite de pérdida diaria de Zero y la regla de consistencia de Standard y Advanced, todo a partir de tus propias operaciones.',
      watch: [
        'Todos los planes siguen tu saldo de cierre y dejan de moverse cuando llegan a tu saldo inicial. Un buen cierre sube tu nivel de pérdida máxima.',
        'Standard limita tu mejor día al 50% de tu ganancia total y Advanced al 40%, así que los días constantes te llevan más rápido que uno grande.',
        'Zero no tiene regla de consistencia en la evaluación, pero sí un límite de pérdida diaria. En un día rojo, revisa cuánto te queda hoy antes de la siguiente operación.',
      ],
    },
  },
  {
    slug: 'earn2trade-trading-journal',
    firm: 'Earn2Trade',
    name: 'Earn2Trade',
    en: {
      intro: 'Track your Earn2Trade Trader Career Path or Gauntlet Mini evaluation with its end-of-day trailing drawdown, its daily loss limit and the 30% consistency rule, updated every time you import your trades.',
      watch: [
        'No single day can be 30% or more of your total profit. That is one of the strictest consistency rules, so a big day can push your real target well past the number on the plan.',
        'The daily loss limit is separate from the max loss. On a red day, check both before the next trade.',
        'The drawdown trails your end-of-day balance and stops once it reaches your starting balance.',
      ],
    },
    es: {
      intro: 'Sigue tu evaluación Trader Career Path o Gauntlet Mini de Earn2Trade con su pérdida máxima móvil de fin de día, su límite de pérdida diaria y la regla de consistencia del 30%, actualizados cada vez que importas tus operaciones.',
      watch: [
        'Ningún día puede ser el 30% o más de tu ganancia total. Es una de las reglas de consistencia más estrictas, así que un día grande puede subir tu objetivo real muy por encima del número del plan.',
        'El límite de pérdida diaria es aparte de la pérdida máxima. En un día rojo, revisa los dos antes de la siguiente operación.',
        'La pérdida máxima sigue tu saldo de cierre y deja de moverse cuando llega a tu saldo inicial.',
      ],
    },
  },
  {
    slug: 'bulenox-trading-journal',
    firm: 'Bulenox',
    name: 'Bulenox',
    en: {
      intro: 'Track your Bulenox Qualification and Master account with the rules of the option you picked: a real-time trailing drawdown on Option 1, or an end-of-day drawdown with a daily loss limit on Option 2.',
      watch: [
        'Option 1 trails in real time, including open profit you give back. Your file only has closed trades, so Market Matrix shows an estimate and reminds you to keep extra room.',
        "Option 2 trails your end-of-day balance and adds a daily loss limit that ends your day when you hit it. Know what's left today before the next trade.",
        'On a Master account the drawdown stops trailing once it reaches your starting balance plus $100.',
      ],
    },
    es: {
      intro: 'Sigue tu Qualification y tu cuenta Master de Bulenox con las reglas de la opción que elegiste: pérdida máxima móvil en tiempo real en la Opción 1, o de fin de día con límite de pérdida diaria en la Opción 2.',
      watch: [
        'La Opción 1 se mueve en tiempo real, incluida la ganancia abierta que devuelves. Tu archivo solo tiene operaciones cerradas, así que Market Matrix muestra una estimación y te recuerda dejar margen extra.',
        'La Opción 2 sigue tu saldo de cierre y agrega un límite de pérdida diaria que termina tu día cuando lo tocas. Sabe cuánto te queda hoy antes de la siguiente operación.',
        'En una cuenta Master la pérdida máxima deja de moverse cuando llega a tu saldo inicial más $100.',
      ],
    },
  },
];

export function findFirmPage(slug) {
  return FIRM_PAGES.find((p) => p.slug === slug);
}

// The full company name, for links and the "not affiliated" note.
export const fullName = (page) => page.fullName || page.name;

// Plan names as shown on the pages: firm product names that are trademarks are described instead.
const PLAN_NAMES = { Combine: 'Evaluation', 'Static Combine': 'Static evaluation', 'Funded (XFA)': 'Funded' };

// The firm's presets as table rows: plan, account size and each rule (null where it doesn't apply).
export function firmRows(presets, firm) {
  return presets.filter((p) => p.firm === firm).map((p) => ({
    plan: (() => {
      const plan = p.label.replace(`${firm} `, '').replace(/^\d+K\s*/, '');
      return PLAN_NAMES[plan] || plan;
    })(),
    size: p.size,
    profitTarget: p.profitTarget || null,
    maxLossLimit: p.maxLossLimit || null,
    drawdownType: p.drawdownType,
    lockOffset: p.trailLockOffset || 0,
    dailyLossLimit: p.dailyLossLimit || null,
    consistency: p.consistencyLimit ? { limit: p.consistencyLimit, base: p.consistencyBase || 'total' } : null,
    payout: p.payoutWinningDays ? { days: p.payoutWinningDays, min: p.payoutMinDayProfit || 0 } : null,
  }));
}

// One firm at a glance, for the all-firms page (PropFirmsPage.js): its plans, the account sizes
// and which kinds of rules its plans use.
export function firmSummary(presets, firm) {
  const list = presets.filter((p) => p.firm === firm);
  const sizes = list.map((p) => p.size).sort((a, b) => a - b);
  const rules = [];
  if (list.some((p) => p.drawdownType === 'trailing_eod')) rules.push('End-of-day trailing');
  if (list.some((p) => p.drawdownType === 'trailing_intraday')) rules.push('Intraday trailing');
  if (list.some((p) => p.drawdownType === 'static')) rules.push('Static');
  if (list.some((p) => p.dailyLossLimit)) rules.push('Daily loss limit');
  if (list.some((p) => p.consistencyLimit)) rules.push('Consistency rule');
  return { plans: [...new Set(firmRows(presets, firm).map((r) => r.plan))], minSize: sizes[0], maxSize: sizes[sizes.length - 1], rules };
}

// The firms named on the homepage; the rest are counted ("and 6 more").
export const FEATURED_FIRMS = ['Topstep', 'Apex', 'MyFundedFutures', 'Lucid'];
